import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { InvitationInterface } from '../../../models/interface/invitation.interface';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HelperService } from '../../../services/helper/helper.service';
import { BehaviorSubject, forkJoin, iif, map, mergeMap, Observable, of, skip, Subscription, take, throwError } from 'rxjs';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { UserInterface } from '../../../models/interface/user.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { StudyActionTypes } from '../../../store/study/study.action';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UserActionTypes } from '../../../store/user/user.action';
import { RoleInterface } from 'src/app/models/interface/role.interface';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

export type PatientInvitationsData = {
  user: UserInterface;
  myUserId: number;
};

@Component({
  selector: 'app-dialog-patient-invitations',
  templateUrl: './dialog-patient-invitations.component.html',
  styleUrls: ['./dialog-patient-invitations.component.scss'],
})
export class DialogPatientInvitationsComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faSearch = faSearch;
  faInfoCircle = faInfoCircle;
  faEnvelope = faEnvelope;
  faCheckCircle = faCheckCircle;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faEdit = faEdit;

  public user: UserInterface;

  public pendingInvitationsData$: Observable<PaginationData<InvitationInterface>>;
  public pendingInvitations$: Observable<InvitationInterface[]>;
  public pendingInvitationsDataSubject = new BehaviorSubject<PaginationData<InvitationInterface>>({ items: [] });
  public pendingInvitationsSubject = new BehaviorSubject<InvitationInterface[]>([]);
  public pagedInvitationsSubject = new BehaviorSubject<InvitationInterface[]>([]);

  public studies: StudyInterface[] = [];
  public includedRoles: RoleInterface[];

  public isLoading$: Observable<boolean>;

  private myUserId: number;

  pagedInvitations$: Observable<InvitationInterface[]>;
  private allInvitations$: Observable<InvitationInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PatientInvitationsData,
    private helperService: HelperService,
    private actions$: Actions,
    private store: Store<{ allInvitations: InvitationInterface[] }>,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.pendingInvitationsData$ = this.pendingInvitationsDataSubject.asObservable();
    this.pendingInvitations$ = this.pendingInvitationsSubject.asObservable();
    this.pagedInvitations$ = this.pagedInvitationsSubject.asObservable();
    this.allInvitations$ = this.store.select('allInvitations');
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.user = this.data.user;
    this.myUserId = this.data.myUserId;
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { type: 'study', include: 'roles' },
    });
    const studyCollaborators = this.actions$.pipe(
      ofType(StudyActionTypes.getCollaboratingStudiesSuccessType, StudyActionTypes.getCollaboratingStudiesErrorType),
      take(1),
      mergeMap((result: any) =>
        iif(
          () => result.type === StudyActionTypes.getCollaboratingStudiesSuccessType,
          of(result),
          throwError(() => result),
        ),
      ),
    );
    this.store.dispatch({
      type: UserActionTypes.getAllInvitationsOfEcoachMembersType,
      payload: { email: this.user.attributes.email }
    });
    const allInvitations = this.allInvitations$.pipe(skip(1), take(1));
    this.subscriptions.push(
      forkJoin({ studyCollaborators, allInvitations })
        .pipe(map((results) => {
          this.studies = results.studyCollaborators.response.body.data;
          return this.performPagination(results.allInvitations);
        }))
        .subscribe({
          next: () => {
            this.isLoadingSubject.next(false);
          },
          error: () => {
            this.studies = [];
            this.pendingInvitationsSubject.next([]);
            this.isLoadingSubject.next(false);
          },
      }),
    );
  }

  removeInvitation(removedInvitation: InvitationInterface): void {
    this.isLoadingSubject.next(true);
    const pendingInvitations = this.pendingInvitationsSubject.value;
    const index = pendingInvitations.findIndex(
      (invitation) => invitation.id === removedInvitation.id,
    );
    if (index === -1) {
      this.isLoadingSubject.next(false);
    } else {
      pendingInvitations.splice(index, 1);
      this.subscriptions.push(
        this.performPagination(pendingInvitations).subscribe(
          () => this.isLoadingSubject.next(false),
        ),
      );
    }
  }

  getIsManager(studyId: number): boolean {
    const study = this.helper.findArrObjById(studyId, this.studies);
    if (!study) {
      return false;
    }
    const highestRole = this.helper.getHighestRoleOfStudy(study);
    return highestRole === 'study.ecoachmanager' || highestRole === 'study.owner';
  }

  getIsECoachOfInvitation(studyId: number, invitation: InvitationInterface): boolean {
    const isSender = invitation.attributes.sender_id.toString() === this.myUserId.toString();
    if (!isSender) {
      return false;
    }
    const study = this.helper.findArrObjById(studyId, this.studies);
    return study && this.helper.getHighestRoleOfStudy(study) === 'study.ecoach';
  }

  updateInvitation(invitation: InvitationInterface) {
    this.isLoadingSubject.next(true);
    this.store.dispatch({
      type: UserActionTypes.getAllInvitationsOfEcoachMembersType,
      payload: { email: this.user.attributes.email },
    });
    this.subscriptions.push(
      this.allInvitations$
        .pipe(map((pendingInvitations) => {
          const notFound = pendingInvitations.every(
            (value) => value.id.toString() !== invitation.id.toString(),
          );
          if (notFound) {
            return of(false);
          } else {
            return this.performPagination(pendingInvitations);
          }
        }))
        .subscribe(() => this.isLoadingSubject.next(false)),
    );
  }

  updatePagedInvitations(event: InvitationInterface[]) {
    if (event) {
      this.pagedInvitationsSubject.next(event);
    }
  }

  updateStudyCode(invitation: InvitationInterface, studyCode: string | null) {
    invitation.attributes.code = studyCode;
  }

  private performPagination(pendingInvitations: InvitationInterface[]) {
    const pageIndex = this.paginator?.paginator ? this.paginator.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator.paginator.pageSize : 20;
    this.pendingInvitationsSubject.next(pendingInvitations);
    return this.helper.setPagedContent(
      this.pendingInvitationsSubject,
      this.pagedInvitationsSubject,
      false,
      pageIndex,
      pageSize,
      this.pendingInvitationsDataSubject,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
